/**
 * SpatialStore — 3D positions for performance mode.
 *
 * Holds per-node world-space positions keyed by instanceId:nodeId.
 * Read synchronously each frame by PerformanceRenderer.
 * Persisted to projects so dragged 3D positions survive save/reload.
 * Mutates in-place to avoid GC pressure at 60fps.
 */

export interface SpatialPosition { x: number; y: number; z: number }

export class SpatialStore {
  private positions = new Map<string, SpatialPosition>();

  private key(instanceId: string, nodeId: string): string {
    return `${instanceId}:${nodeId}`;
  }

  get(instanceId: string, nodeId: string): SpatialPosition | undefined {
    return this.positions.get(this.key(instanceId, nodeId));
  }

  set(instanceId: string, nodeId: string, x: number, y: number, z: number): void {
    const k = this.key(instanceId, nodeId);
    const existing = this.positions.get(k);
    if (existing) { existing.x = x; existing.y = y; existing.z = z; }
    else { this.positions.set(k, { x, y, z }); }
  }

  delete(instanceId: string, nodeId: string): void {
    this.positions.delete(this.key(instanceId, nodeId));
  }

  clear(): void { this.positions.clear(); }

  /** Serialize for project save. Keyed by "instanceId:nodeId" → {x,y,z}. */
  toJSON(): Record<string, SpatialPosition> {
    const out: Record<string, SpatialPosition> = {};
    for (const [k, v] of this.positions) {
      out[k] = { x: v.x, y: v.y, z: v.z };
    }
    return out;
  }

  /** Restore from project load. */
  fromJSON(data: Record<string, SpatialPosition>): void {
    this.positions.clear();
    for (const [k, v] of Object.entries(data)) {
      this.positions.set(k, { x: v.x, y: v.y, z: v.z });
    }
  }
}
